import clsx from 'clsx';
import { PropsWithChildren, ReactNode, useState } from 'react';
import EditIcon from '@/assets/icons/edit-01.svg?react';
import { CheckIcon } from 'lucide-react';
import { Input } from '../ui/input';

interface StyledFormHeaderWithBreadcrumbProps {
  title: string;
  setTitle?: (title: string) => void;
  breadcrumb: ReactNode;
  code?: string;
  subTitle?: string;
  label?: ReactNode;
  sticky?: boolean;
  subHeading?: string;
  underNode?: ReactNode;
  titleMuted?: boolean;
}

const StyledFormHeaderWithBreadcrumb = ({
  title,
  setTitle,
  breadcrumb,
  label,
  code,
  subTitle,
  children,
  sticky = false,
  subHeading,
  underNode,
  titleMuted = false,
}: PropsWithChildren<StyledFormHeaderWithBreadcrumbProps>) => {
  const [isEditEnabled, setIsEditEnabled] = useState(false);

  return (
    <div
      className={clsx('flex flex-col w-full p-6 gap-6', {
        'sticky top-0 md:top-14 bg-brand-25 z-10': sticky,
      })}
    >
      <div>{breadcrumb}</div>
      <div className="flex flex-col md:flex-row w-full justify-between h-auto">
        <div className="flex flex-col gap-2">
          {subHeading && (
            <p className="text-base font-medium text-brand-600">{subHeading}</p>
          )}
          <div className="flex flex-col md:flex-row items-center gap-4">
            {isEditEnabled ? (
              <>
                <Input
                  type="text"
                  className="lg:text-2xl rounded-lg border-gray-300 gap-2 text-gray-900 focus-visible:outline-hidden focus-visible:shadow-active focus-visible:ring-offset-2 focus-visible:border-brand-300"
                  value={title}
                  onChange={(e) => setTitle?.(e.target.value)}
                />
                <CheckIcon
                  className="w-5 h-5 stroke-brand-700 hover:cursor-pointer"
                  onClick={() => setIsEditEnabled(false)}
                />
              </>
            ) : (
              <>
                <h1
                  className={clsx('text-3xl font-semibold break-all', {
                    'text-gray-500': titleMuted,
                    'text-gray-950': !titleMuted,
                  })}
                >
                  {code && title
                    ? `${code} - ${title}`
                    : code || title}
                </h1>
                {setTitle !== undefined && (
                  <EditIcon
                    className="w-5 h-5 stroke-brand-700 hover:cursor-pointer"
                    onClick={() => setIsEditEnabled(true)}
                  />
                )}
                {label}
              </>
            )}
          </div>
          {subTitle && (
            <p className="text-sm leading-4 text-gray-600">{subTitle}</p>
          )}
        </div>
        <div className="flex flex-col md:flex-row gap-4">{children}</div>
      </div>
      {underNode ? <div>{underNode}</div> : null}
    </div>
  );
};

export default StyledFormHeaderWithBreadcrumb;
